import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * "Tell me what is on this screen" — one key, on every screen.
 *
 *
 * WHY THIS EXISTS
 *
 * Everything else in this effort makes controls announce themselves AS YOU
 * REACH THEM. That answers "what is this thing I just landed on" and never
 * answers "what is on this screen" — for which the only method was to tab
 * through the whole screen and assemble it from memory. On a tray ticket or a
 * meal pattern, which are read far more often than they are edited, that is
 * the entire task made needlessly serial.
 *
 * Sighted users get the whole screen at a glance. This is the spoken
 * equivalent: press once, hear what is here.
 *
 *
 * SUMMARY FIRST, DETAIL ON DEMAND
 *
 * Reading everything on one press is the obvious design and the wrong one: a
 * twenty-item meal pattern becomes a wall of speech she cannot skim, stop, or
 * re-enter partway. So the first press gives shape and counts, and each
 * further press steps into the next section. She chooses the depth.
 *
 * Escape resets to the top. A route change resets too, so a summary can never
 * describe a screen she has left.
 *
 *
 * WHERE THE TEXT COMES FROM
 *
 * The screen supplies it from its own data, not by scraping the DOM. Reading
 * the rendered text back would inherit exactly the run-together layout that
 * made the tray ticket unreadable in the first place, and would break whenever
 * the visual arrangement changed.
 */

export interface ScreenSummarySection {
  /** Spoken as the section's heading — "Items", "Notices". */
  label: string;
  /** One utterance each, in reading order. */
  lines: string[];
}

export interface ScreenSummaryValue {
  /** Shape and counts. Spoken on the first press. */
  summary: string;
  /** Stepped through, one per subsequent press. */
  sections?: ScreenSummarySection[];
}

/**
 * A STACK, not a single value.
 *
 * Screens nest: the meal pattern tab lives inside Resident Detail, and both
 * describe themselves. With one slot the last writer won and F2 answered for
 * whichever mounted most recently — which is usually right, but the loser
 * vanished entirely and the winner changed as tabs came and went.
 *
 * The innermost entry answers, and popping it restores the one around it, so
 * leaving a tab returns F2 to the record rather than to nothing.
 */
type Entry = ScreenSummaryValue & { path: string; token: symbol };
const stack: Entry[] = [];
const listeners = new Set<() => void>();

function activeEntry(pathname: string): Entry | null {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    if (stack[i].path === pathname) return stack[i];
  }
  return null;
}

/** Bumped to ask the mounted announcer to speak the next part. */
let advanceSignal = 0;
const advanceListeners = new Set<() => void>();

/**
 * Speaks the next part of this screen's summary.
 *
 * Exported rather than bound to a key here, because the key belongs to the
 * shortcut registry and a key must have exactly ONE owner. Registering F2 for
 * documentation with a no-op handler, alongside a private window listener that
 * did the real work, meant the registry CLAIMED the key and swallowed it — the
 * failure useKeyboardShortcuts.ts already documents ("a claimed key is a
 * swallowed key... which is how Escape stopped working app-wide"). F2 then did
 * nothing at all on any screen.
 */
export function advanceScreenSummary(): void {
  advanceSignal += 1;
  advanceListeners.forEach((fn) => fn());
}

/** Returns to the summary. Wired to Escape by the same registry entry. */
export function resetScreenSummary(): void {
  advanceSignal = 0;
  advanceListeners.forEach((fn) => fn());
}

/**
 * Registers what this screen would say about itself.
 *
 * Pass null while the data is still loading — a confident summary of a
 * half-loaded screen is worse than no summary.
 */
export function useScreenSummary(value: ScreenSummaryValue | null): void {
  const key = value ? value.summary + '|' + (value.sections ?? []).map(
    (s) => s.label + ':' + s.lines.length).join(',') : '';
  const token = useRef<symbol>(Symbol('screen-summary'));

  useEffect(() => {
    const mine = token.current;
    // Drop any previous entry of MINE before pushing the new one, so a screen
    // whose data updates replaces itself rather than stacking copies of
    // itself and making F2 depend on how many times it re-rendered.
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].token === mine) stack.splice(i, 1);
    }
    if (value) {
      stack.push({ ...value, path: window.location.pathname, token: mine });
    }
    listeners.forEach((fn) => fn());
    return () => {
      for (let i = stack.length - 1; i >= 0; i -= 1) {
        if (stack[i].token === mine) stack.splice(i, 1);
      }
      listeners.forEach((fn) => fn());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** True when the current screen has supplied a summary. Used for the hint. */
export function hasScreenSummary(pathname: string): boolean {
  return activeEntry(pathname) !== null;
}

export default function ScreenSummaryAnnouncer() {
  const location = useLocation();
  const [message, setMessage] = useState('');
  // -1 = nothing said yet. 0 = summary. 1..n = sections.
  const step = useRef(-1);
  // Which summary the step counter belongs to. Switching sections swaps the
  // answering summary without changing the route, and a counter carried across
  // that boundary made F2 resume mid-walk on a different screen — pressing it
  // once on the meal pattern answered "Lunch. 1. Menu item 1", a detail
  // section, rather than the summary.
  // Identified by CONTENT, not by the component that supplied it. One
  // component can answer for several screens — Resident Detail describes
  // whichever section is open — so a per-instance token never changed as she
  // moved between them, and F2 resumed mid-walk: pressing it on Diet Order
  // read out the section list left over from the record.
  const stepKey = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, bump] = useState(0);

  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);

  useEffect(() => { step.current = -1; }, [location.pathname]);

  useEffect(() => {
    const say = (text: string) => {
      // Blanked first: aria-live fires on CHANGE, and stepping past the last
      // section back to the summary is the same string twice.
      setMessage('');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setMessage(text), 60);
    };

    const speak = () => {
      if (advanceSignal === 0) { step.current = -1; return; }

      const value = activeEntry(location.pathname);
      const key = value ? value.summary : null;
      if (key !== stepKey.current) {
        stepKey.current = key;
        step.current = -1;
      }
      if (!value) {
        say('This screen has no summary yet. Press Tab to move through it.');
        return;
      }

      const sections = value.sections ?? [];
      step.current += 1;
      if (step.current === 0) {
        const more = sections.length > 0
          ? ` Press F2 again for detail, ${sections.length} `
            + `${sections.length === 1 ? 'section' : 'sections'}.`
          : '';
        say(value.summary + more);
        return;
      }

      const section = sections[step.current - 1];
      if (!section) {
        // Past the end — wrap to the summary rather than going silent, which
        // is indistinguishable from the key not working.
        step.current = 0;
        say(value.summary);
        return;
      }
      say(`${section.label}. ${section.lines.join('. ')}.`);
    };

    advanceListeners.add(speak);
    return () => { advanceListeners.delete(speak); };
  }, [location.pathname]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return (
    <div
      // Several polite regions exist (toasts, page arrival). This one is
      // identifiable so a test can assert what F2 said rather than whichever
      // region happened to update last — a distinction that made an early
      // version of the meal-pattern test report the arrival orientation as if
      // it were the summary.
      data-screen-summary=""
      className="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {message}
    </div>
  );
}
